import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Ported from @zph/zph-component-library so the app no longer depends on the
// internal registry. The class *sets*, size names, default size and the wrapping
// <div> match the published 0.1.5 component, so rendering is unchanged — only the
// base class order differs (prettier-plugin-tailwindcss sorts it, and the classes
// are in distinct property groups so order carries no meaning here).
// `text-medium` (14px) and `text-large` (20px) come from this app's own
// tailwind.config.js fontSize extension.
const titleVariants = cva("mb-1 font-medium", {
  variants: {
    size: {
      lg: "text-xl leading-relaxed",
      md: "text-large leading-snug",
      sm: "text-medium leading-normal",
      xsm: "text-sm leading-normal",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

/** The heading elements a Title may render as. */
export type HeadingLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

export type TitleProps = React.HTMLAttributes<HTMLHeadingElement> &
  VariantProps<typeof titleVariants> & {
    /**
     * The heading level this title occupies in the page outline.
     *
     * Deliberately **required**. This component used to hard-code `<h1>`, so every
     * screen that rendered more than one Title emitted several level-one headings and
     * no sub-levels — a screen reader user navigating by heading got a flat list with
     * no structure. There is no safe default: the correct level depends on what the
     * title sits under on that particular page, which only the call site knows. A
     * required prop turns that into a compile error instead of a silent wrong outline.
     *
     * `size` controls appearance only. Level and size are independent on purpose — a
     * visually small heading can still be an h2.
     */
    as: HeadingLevel;
  };

const Title = React.forwardRef<HTMLHeadingElement, TitleProps>(
  ({ className, size, as: Heading, ...props }, ref) => {
    return (
      <div>
        {/* Hidden component-identity marker; .random-unique-id is `@apply hidden`
            in App.css. Same convention as pages/components/input.tsx. */}
        <span className="random-unique-id">Title-001</span>
        <Heading
          ref={ref}
          className={cn(titleVariants({ size }), className)}
          {...props}
        />
      </div>
    );
  },
);

Title.displayName = "Title";

export { Title };
